import { useEffect, useMemo, useRef, useState } from 'react';
import { KioskHero } from '../weighing/kiosk/KioskHero';
import { apiUrl } from '../../shared/api/apiConfig';
import { useAuth } from '../../shared/auth/AuthContext';
import { getUserAvatarLabel } from '../../shared/auth/avatarLabel';
import { getUserDisplayName } from '../../shared/auth/userDisplayName';
import { formatDisplayDateTime } from '../../shared/utils/dateFormat';
import './PalletRegistrationsPage.css';

const WOOD_PALLET_TYPE = '\u0E44\u0E21\u0E49';
const PLASTIC_PALLET_TYPE = '\u0E1E\u0E25\u0E32\u0E2A\u0E15\u0E34\u0E01';
const PALLET_TYPE_OPTIONS = [WOOD_PALLET_TYPE, PLASTIC_PALLET_TYPE];
const PALLET_STATUS_OPTIONS = ['Active', 'Inactive'];
const PAGE_SIZE = 20;
const EXPORT_FETCH_SIZE = 200;
const SEARCH_DEBOUNCE_MS = 300;

function createEmptyForm(registeredBy = '') {
  return {
    palletType: WOOD_PALLET_TYPE,
    weight: '0.001',
    registeredBy,
    notes: '',
    status: 'Active',
  };
}

const EMPTY_FORM = {
  palletType: WOOD_PALLET_TYPE,
  weight: '0.001',
  registeredBy: '',
  notes: '',
  status: 'Active',
};

const EMPTY_BULK_FORM = {
  palletType: WOOD_PALLET_TYPE,
  weight: '0.001',
  count: '1',
};

function sortRegistrations(items) {
  return [...items].sort((left, right) => {
    const timeDifference = new Date(right.registeredAtUtc) - new Date(left.registeredAtUtc);
    if (timeDifference !== 0) {
      return timeDifference;
    }

    return String(right.generatedCode).localeCompare(String(left.generatedCode));
  });
}

function buildRegistrationsRequestUrl(search, skip = 0, take = PAGE_SIZE) {
  const params = new URLSearchParams({
    skip: String(skip),
    take: String(take),
  });

  const normalizedSearch = search.trim();
  if (normalizedSearch) {
    params.set('search', normalizedSearch);
  }

  return `${apiUrl('/palletregistrations')}?${params.toString()}`;
}

function normalizePagedRegistrationsPayload(payload, take = PAGE_SIZE) {
  if (Array.isArray(payload)) {
    return {
      items: sortRegistrations(payload),
      totalCount: payload.length,
      skip: 0,
      take,
      hasMore: false,
    };
  }

  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    totalCount: Number.isFinite(payload?.totalCount) ? payload.totalCount : 0,
    skip: Number.isFinite(payload?.skip) ? payload.skip : 0,
    take: Number.isFinite(payload?.take) ? payload.take : take,
    hasMore: Boolean(payload?.hasMore),
  };
}

function formatDate(value) {
  return formatDisplayDateTime(value, 'Unknown');
}

function buildQrPreviewUrl(registration) {
  return registration?.qrCodeDataUrl ?? registration?.qrCodeUrl ?? '';
}

function buildFormFromRegistration(registration, defaultRegisteredBy = '') {
  return {
    palletType: registration?.palletType ?? '',
    weight: String(registration?.weight ?? '0.001'),
    registeredBy: defaultRegisteredBy || registration?.registeredBy || '',
    notes: registration?.notes ?? '',
    status: registration?.status ?? 'Active',
  };
}

function statusClassName(value) {
  return String(value ?? 'Unknown').toLowerCase();
}

function buildExportRows(items) {
  return items.map((registration, index) => ({
    'No.': index + 1,
    'Generated Code': registration.generatedCode ?? '',
    'Pallet Type': registration.palletType ?? '',
    Weight: registration.weight ?? '',
    'Registered By': registration.registeredBy ?? '',
    Status: registration.status ?? '',
    'Registered At': formatDate(registration.registeredAtUtc),
    Notes: registration.notes ?? '',
  }));
}

function buildExportFileName() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  return `pallet-registrations-${year}-${month}-${day}.xlsx`;
}

function buildQrImagesFileName() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  return `pallet-qr-images-${year}-${month}-${day}.zip`;
}

function getImageExtension(contentType) {
  if (contentType === 'image/jpeg') {
    return 'jpg';
  }

  if (contentType === 'image/svg+xml') {
    return 'svg';
  }

  if (contentType === 'image/webp') {
    return 'webp';
  }

  return 'png';
}

function buildQrImageFileName(registration, index, contentType) {
  const safeCode = String(registration.generatedCode || `pallet-${index + 1}`)
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '-');

  const sequence = String(index + 1).padStart(3, '0');
  return `${sequence}-${safeCode || `pallet-${index + 1}`}.${getImageExtension(contentType)}`;
}

const EXPORT_COLUMNS = [
  { header: 'No.', key: 'No.', width: 8 },
  { header: 'Generated Code', key: 'Generated Code', width: 18 },
  { header: 'Pallet Type', key: 'Pallet Type', width: 16 },
  { header: 'Weight', key: 'Weight', width: 12 },
  { header: 'Registered By', key: 'Registered By', width: 20 },
  { header: 'Status', key: 'Status', width: 14 },
  { header: 'Registered At', key: 'Registered At', width: 24 },
  { header: 'Notes', key: 'Notes', width: 36 },
];

function downloadBlob(blob, fileName) {
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = downloadUrl;
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(downloadUrl);
}

function downloadExcelFile(buffer, fileName) {
  const blob = new Blob(
    [buffer],
    { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  );

  downloadBlob(blob, fileName);
}

async function exportRegistrationsToExcel(items) {
  const ExcelJS = await import('exceljs');
  const Workbook = ExcelJS.Workbook ?? ExcelJS.default?.Workbook;
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet('Pallets');

  worksheet.columns = EXPORT_COLUMNS;
  worksheet.addRows(buildExportRows(items));

  const buffer = await workbook.xlsx.writeBuffer();
  downloadExcelFile(buffer, buildExportFileName());
}

async function downloadRegistrationImagesAsZip(items) {
  const ids = items.map((registration) => registration.id);

  const response = await fetch(apiUrl('/palletregistrations/qr-archive'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });

  if (!response.ok) {
    throw new Error('Unable to download QR archive.');
  }

  const blob = await response.blob();
  downloadBlob(blob, buildQrImagesFileName());
}

export default function PalletRegistrationsPage() {
  const { user } = useAuth();
  const actorName = getUserDisplayName(user);
  const avatarLabel = getUserAvatarLabel(user);
  const [registrations, setRegistrations] = useState([]);
  const [form, setForm] = useState(() => createEmptyForm(actorName));
  const [listFilter, setListFilter] = useState('');
  const [debouncedListFilter, setDebouncedListFilter] = useState('');
  const [editingRegistrationId, setEditingRegistrationId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloadingImages, setIsDownloadingImages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMoreRegistrations, setHasMoreRegistrations] = useState(false);
  const [loadState, setLoadState] = useState({ type: 'idle', message: '' });
  const [saveState, setSaveState] = useState({ type: 'idle', message: '' });
  const [bulkForm, setBulkForm] = useState(EMPTY_BULK_FORM);
  const [bulkState, setBulkState] = useState({ type: 'idle', message: '' });
  const [imageDownloadState, setImageDownloadState] = useState({ type: 'idle', message: '' });
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const isPageLoading = loadState.type === 'pending' && !hasLoadedOnce;
  const pageLoadingMessage = loadState.message || 'Loading registered pallets...';
  const sentinelRef = useRef(null);
  const listRef = useRef(null);
  const latestSearchRef = useRef('');
  const loadSequenceRef = useRef(0);

  useEffect(() => {
    setForm((current) => (
      current.registeredBy === actorName
        ? current
        : { ...current, registeredBy: actorName }
    ));
  }, [actorName]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedListFilter(listFilter.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [listFilter]);

  async function fetchRegistrationsPage(search, skip, take = PAGE_SIZE, signal) {
    const response = await fetch(buildRegistrationsRequestUrl(search, skip, take), { signal });
    if (!response.ok) {
      throw new Error('Failed to load pallet registrations.');
    }

    return normalizePagedRegistrationsPayload(await response.json(), take);
  }

  async function fetchAllMatchingRegistrations(search) {
    const items = [];
    let skip = 0;
    let hasMore = true;

    while (hasMore) {
      const page = await fetchRegistrationsPage(search, skip, EXPORT_FETCH_SIZE);
      items.push(...page.items);
      hasMore = page.hasMore;
      skip += page.items.length;

      if (page.items.length === 0) {
        break;
      }
    }

    return items;
  }

  async function refreshRegistrations(search, signal) {
    const page = await fetchRegistrationsPage(search, 0, PAGE_SIZE, signal);
    setRegistrations(page.items);
    setTotalCount(page.totalCount);
    setHasMoreRegistrations(page.hasMore);
    return page;
  }

  useEffect(() => {
    const controller = new AbortController();
    const loadSequence = ++loadSequenceRef.current;
    latestSearchRef.current = debouncedListFilter;
    setIsLoadingMore(false);
    setLoadState((current) => (
      hasLoadedOnce
        ? { ...current, message: '' }
        : { type: 'pending', message: 'Loading registered pallets...' }
    ));

    refreshRegistrations(debouncedListFilter, controller.signal).then(() => {
      if (controller.signal.aborted || loadSequence !== loadSequenceRef.current) {
        return;
      }

      setHasLoadedOnce(true);
      setLoadState({ type: 'success', message: '' });
    }).catch((error) => {
      if (controller.signal.aborted || loadSequence !== loadSequenceRef.current) {
        return;
      }

      if (!hasLoadedOnce) {
        setRegistrations([]);
        setTotalCount(0);
        setHasMoreRegistrations(false);
      }
      setLoadState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load pallet registrations.',
      });
    });

    return () => controller.abort();
  }, [debouncedListFilter]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (typeof IntersectionObserver === 'undefined' || !sentinel || !hasMoreRegistrations || isPageLoading || isLoadingMore) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || isLoadingMore) {
          return;
        }

        setIsLoadingMore(true);
        fetchRegistrationsPage(latestSearchRef.current, registrations.length).then((page) => {
          if (latestSearchRef.current !== debouncedListFilter) {
            return;
          }

          setRegistrations((current) => [...current, ...page.items]);
          setTotalCount(page.totalCount);
          setHasMoreRegistrations(page.hasMore);
        }).catch((error) => {
          setLoadState({
            type: 'error',
            message: error instanceof Error ? error.message : 'Failed to load more pallet registrations.',
          });
        }).finally(() => {
          setIsLoadingMore(false);
        });
      },
      { root: listRef.current, rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [debouncedListFilter, hasMoreRegistrations, isLoadingMore, isPageLoading, registrations.length]);

  const hasActiveSearch = debouncedListFilter.length > 0;
  const visibleRegistrations = registrations;
  const displayedCount = registrations.length;
  const visibleCount = displayedCount;
  const filteredRegistrations = { length: totalCount };

  const editingRegistration = useMemo(
    () => registrations.find((registration) => registration.id === editingRegistrationId) ?? null,
    [editingRegistrationId, registrations],
  );
  const isEditingRegistration = Boolean(editingRegistrationId);

  async function refreshAfterMutation(search = listFilter.trim()) {
    const normalizedSearch = search.trim();
    latestSearchRef.current = normalizedSearch;

    const page = await refreshRegistrations(normalizedSearch);
    setDebouncedListFilter(normalizedSearch);
    setLoadState({ type: 'success', message: '' });
    return page;
  }

  function handleFieldChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function resetForm(clearFeedback = true) {
    setForm(createEmptyForm(actorName));
    setEditingRegistrationId(null);
    if (clearFeedback) {
      setSaveState({ type: 'idle', message: '' });
    }
  }

  function startEditingRegistration(registration) {
    setEditingRegistrationId(registration.id);
    setForm(buildFormFromRegistration(registration, actorName));
    setSaveState({ type: 'idle', message: '' });
  }

  function closeEditModal() {
    resetForm();
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const isEditing = Boolean(editingRegistrationId);

    const payload = {
      palletType: form.palletType.trim(),
      weight: Number.parseFloat(form.weight),
      registeredBy: form.registeredBy.trim(),
      notes: form.notes.trim(),
      status: form.status,
    };

    if (!PALLET_TYPE_OPTIONS.includes(payload.palletType) || !Number.isFinite(payload.weight) || payload.weight <= 0) {
      setSaveState({
        type: 'error',
        message: 'Pallet type must be ไม้ or พลาสติก, and weight is required.',
      });
      return;
    }

    setSaveState({
      type: 'pending',
      message: isEditing
        ? 'Saving pallet changes...'
        : 'Registering pallet and generating QR code...',
    });

    try {
      const response = await fetch(
        isEditing
          ? apiUrl(`/palletregistrations/${encodeURIComponent(editingRegistrationId)}`)
          : apiUrl('/palletregistrations'),
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message ?? (isEditing ? 'Unable to update pallet.' : 'Unable to register pallet.'));
      }

      const saved = await response.json();
      await refreshAfterMutation(listFilter);
      setSaveState({
        type: 'success',
        message: isEditing
          ? `Pallet ${saved.generatedCode} updated successfully.`
          : `Pallet registered with code ${saved.generatedCode}. The QR code is ready to download.`,
      });
      resetForm(false);
    } catch (error) {
      setSaveState({
        type: 'error',
        message: error instanceof Error ? error.message : (isEditing ? 'Unable to update pallet.' : 'Unable to register pallet.'),
      });
    }
  }

  function handleBulkFieldChange(event) {
    const { name, value } = event.target;
    setBulkForm((current) => ({ ...current, [name]: value }));
  }

  async function handleBulkSubmit() {
    const count = Number.parseInt(bulkForm.count, 10);
    const weight = Number.parseFloat(bulkForm.weight);

    if (!PALLET_TYPE_OPTIONS.includes(bulkForm.palletType) || !Number.isInteger(count) || count < 1 || count > 100 || !Number.isFinite(weight) || weight <= 0) {
      setBulkState({ type: 'error', message: 'กรุณาเลือกประเภทพาเลท ระบุน้ำหนัก และจำนวน 1–100' });
      return;
    }

    setBulkState({ type: 'pending', message: `กำลังลงทะเบียนพาเลท ${count} รายการ...` });

    try {
      const response = await fetch(apiUrl('/palletregistrations/bulk'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ palletType: bulkForm.palletType, weight, count }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message ?? 'ไม่สามารถลงทะเบียนพาเลทเป็นกลุ่มได้');
      }

      const created = await response.json();
      await refreshAfterMutation(listFilter);
      setBulkState({
        type: 'success',
        message: `ลงทะเบียนพาเลทสำเร็จ ${created.length} รายการ (สถานะ Inactive)`,
      });
      setBulkForm(EMPTY_BULK_FORM);
    } catch (error) {
      setBulkState({
        type: 'error',
        message: error instanceof Error ? error.message : 'ไม่สามารถลงทะเบียนพาเลทเป็นกลุ่มได้',
      });
    }
  }

  async function handleBulkDownloadImages() {
    if (totalCount === 0 || isDownloadingImages) {
      return;
    }

    setIsDownloadingImages(true);
    setImageDownloadState({
      type: 'pending',
      message: `Preparing ${totalCount} QR image${totalCount === 1 ? '' : 's'}...`,
    });

    try {
      const matchingRegistrations = await fetchAllMatchingRegistrations(debouncedListFilter);
      await downloadRegistrationImagesAsZip(matchingRegistrations);
      setImageDownloadState({
        type: 'success',
        message: `Downloaded ${matchingRegistrations.length} QR image${matchingRegistrations.length === 1 ? '' : 's'}.`,
      });
    } catch (error) {
      setImageDownloadState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to download QR images.',
      });
    } finally {
      setIsDownloadingImages(false);
    }
  }

  async function handleExportToExcel() {
    if (totalCount === 0 || isExporting) {
      return;
    }

    setIsExporting(true);

    try {
      const matchingRegistrations = await fetchAllMatchingRegistrations(debouncedListFilter);
      await exportRegistrationsToExcel(matchingRegistrations);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <section className="pallet-page">
      <KioskHero
        eyebrow="การลงทะเบียนพาเลท"
        title="ทะเบียนพาเลท"
        avatarLabel={avatarLabel}
      />

      {isPageLoading ? (
        <div className="pallet-loading-block" role="status" aria-live="polite">
          <div className="pallet-loading-spinner" aria-hidden="true" />
          <p>{pageLoadingMessage}</p>
        </div>
      ) : (
      <div className="pallet-grid" aria-hidden={editingRegistration ? 'true' : undefined}>
        <div className="pallet-form-stack">
        <form className="pallet-card pallet-form-card" onSubmit={handleSubmit}>
          <div className="pallet-card-header">
            <div>
              <h2>ลงทะเบียนพาเลทใหม่</h2>
              <p>การบันทึกแต่ละครั้งจะสร้างรหัสพาเลทเฉพาะและภาพ QR ที่คุณสามารถดาวน์โหลดได้ในภายหลัง</p>
            </div>
          </div>
          <div className="pallet-form-row">
            <label className="pallet-field">
              <span>{isEditingRegistration ? 'ประเภทพาเลทใหม่' : 'ประเภทพาเลท'}</span>
              <select
                name="palletType"
                value={form.palletType}
                onChange={handleFieldChange}
                disabled={isEditingRegistration}
              >
                {PALLET_TYPE_OPTIONS.map((palletType) => (
                  <option key={palletType} value={palletType}>
                    {palletType}
                  </option>
                ))}
              </select>
            </label>

            <label className="pallet-field">
              <span>{isEditingRegistration ? 'น้ำหนักพาเลทใหม่' : 'น้ำหนัก'}</span>
              <input
                name="weight"
                type="number"
                min="0.001"
                step="0.001"
                value={form.weight}
                onChange={handleFieldChange}
              />
            </label>
          </div>

          <label className="pallet-field">
            <span>{isEditingRegistration ? 'ลงทะเบียนพาเลทใหม่โดย' : 'ลงทะเบียนโดย'}</span>
            <input
              name="registeredBy"
              value={form.registeredBy}
              onChange={handleFieldChange}
              placeholder="e.g. Warehouse Team"
            />
          </label>

          <label className="pallet-field">
            <span>{isEditingRegistration ? 'สถานะพาเลทใหม่' : 'สถานะ'}</span>
            <select name="status" value={form.status} onChange={handleFieldChange}>
              {PALLET_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>

          <label className="pallet-field">
            <span>{isEditingRegistration ? 'หมายเหตุพาเลทใหม่' : 'หมายเหตุ'}</span>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleFieldChange}
              rows={4}
              placeholder="หมายเหตุการจัดการหรือการจัดส่ง (ไม่บังคับ)"
            />
          </label>

          <div className="pallet-actions">
            <button type="submit" className="pallet-primary-btn">
              สร้าง QR
            </button>
            <button type="button" className="pallet-secondary-btn" onClick={() => resetForm()}>
              รีเซ็ตฟอร์ม
            </button>
          </div>

          {saveState.message ? (
            <p className={`pallet-feedback pallet-feedback-${saveState.type}`}>{saveState.message}</p>
          ) : null}
        </form>

          <section className="pallet-card pallet-bulk-card">
          <div className="pallet-card-header">
            <div>
              <h2>ลงทะเบียนพาเลทเป็นกลุ่ม</h2>
              <p>สร้างพาเลทหลายรายการพร้อมกัน โดยสถานะเริ่มต้นจะเป็น Inactive</p>
            </div>
          </div>

          <div className="pallet-form-row">
            <label className="pallet-field">
              <span>ประเภทพาเลท (กลุ่ม)</span>
              <select
                name="palletType"
                value={bulkForm.palletType}
                onChange={handleBulkFieldChange}
              >
                {PALLET_TYPE_OPTIONS.map((palletType) => (
                  <option key={palletType} value={palletType}>
                    {palletType}
                  </option>
                ))}
              </select>
            </label>

            <label className="pallet-field">
              <span>น้ำหนัก (กลุ่ม)</span>
              <input
                name="weight"
                type="number"
                min="0.001"
                step="0.001"
                value={bulkForm.weight}
                onChange={handleBulkFieldChange}
              />
            </label>

            <label className="pallet-field">
              <span>จำนวนพาเลท (1–100)</span>
              <input
                name="count"
                type="number"
                min="1"
                max="100"
                step="1"
                value={bulkForm.count}
                onChange={handleBulkFieldChange}
              />
            </label>
          </div>

          <div className="pallet-actions">
            <button
              type="button"
              className="pallet-primary-btn"
              disabled={bulkState.type === 'pending'}
              onClick={handleBulkSubmit}
            >
              {bulkState.type === 'pending' ? 'กำลังสร้าง...' : 'ลงทะเบียนพาเลทเป็นกลุ่ม'}
            </button>
          </div>

          {bulkState.message ? (
            <p className={`pallet-feedback pallet-feedback-${bulkState.type}`}>{bulkState.message}</p>
          ) : null}
          </section>
        </div>

        <div className="pallet-card pallet-list-card">
          <div className="pallet-list-toolbar">
            <label className="pallet-filter-field">
              <span>กรองพาเลท</span>
              <input
                value={listFilter}
                onChange={(event) => setListFilter(event.target.value)}
                placeholder="ค้นหาตามรหัส, ประเภท, สถานะ, หมายเหตุ, หรือผู้ลงทะเบียน"
              />
            </label>

            <button
              type="button"
              className="pallet-secondary-btn pallet-export-btn"
              onClick={handleBulkDownloadImages}
              disabled={isDownloadingImages || totalCount === 0 || loadState.type === 'error'}
            >
              {isDownloadingImages ? 'Preparing images...' : 'Download QR images'}
            </button>

            <button
              type="button"
              className="pallet-secondary-btn pallet-export-btn"
              onClick={handleExportToExcel}
              disabled={isExporting || totalCount === 0 || loadState.type === 'error'}
            >
              {isExporting ? 'กำลังส่งออก...' : 'ส่งออกเป็น Excel'}
            </button>
          </div>

          {imageDownloadState.message ? (
            <p className={`pallet-feedback pallet-feedback-${imageDownloadState.type}`}>{imageDownloadState.message}</p>
          ) : null}

          {loadState.type === 'error' ? (
            <div className="pallet-empty">
              <strong>ไม่สามารถโหลดการลงทะเบียนพาเลทได้</strong>
              <p>{loadState.message}</p>
            </div>
          ) : displayedCount === 0 && !hasActiveSearch ? (
            <div className="pallet-empty">
              <strong>ยังไม่มีการลงทะเบียนพาเลท</strong>
              <p>รายการจะแสดงที่นี่ทันทีที่มีการลงทะเบียนพาเลท</p>
            </div>
          ) : displayedCount === 0 ? (
            <div className="pallet-empty">
              <strong>ไม่มีพาเลทที่ตรงกับตัวกรองปัจจุบัน</strong>
              <p>ลองค้นหาตามรหัสที่สร้างขึ้น, ประเภทพาเลท, สถานะ, หมายเหตุ, หรือผู้ลงทะเบียน</p>
            </div>
          ) : (
            <>
              <p className="pallet-filter-summary">
                แสดง {visibleRegistrations.length} จาก {filteredRegistrations.length}
                {filteredRegistrations.length !== registrations.length ? ` (กรองจาก ${registrations.length})` : ''} พาเลทที่ลงทะเบียนแล้ว
              </p>
              <div className="pallet-list" ref={listRef}>
                {visibleRegistrations.map((registration) => (
                  <article key={registration.id} className="pallet-item">
                    <img
                      className="pallet-item-image"
                      src={buildQrPreviewUrl(registration)}
                      alt={`QR code for ${registration.generatedCode}`}
                      loading="lazy"
                    />
                    <div className="pallet-item-content">
                      <div className="pallet-item-top">
                        <div>
                          <span className="pallet-code-pill">{registration.generatedCode}</span>
                          <span className={`pallet-status-pill pallet-status-${statusClassName(registration.status)}`}>
                            {registration.status ?? 'Unknown'}
                          </span>
                          <h3>{registration.palletType}</h3>
                        </div>
                        <div className="pallet-item-actions">
                          <button
                            type="button"
                            className="pallet-secondary-btn"
                            onClick={() => startEditingRegistration(registration)}
                          >
                            แก้ไขพาเลท
                          </button>
                          <a className="pallet-download-link" href={registration.qrCodeDownloadUrl}>
                            ดาวน์โหลดรหัส QR
                          </a>
                        </div>
                      </div>

                      <dl className="pallet-meta-grid">
                        <div>
                          <dt>น้ำหนัก</dt>
                          <dd className="pallet-weight-value">{registration.weight}</dd>
                        </div>
                        <div>
                          <dt>ลงทะเบียนโดย</dt>
                          <dd>{registration.registeredBy || 'ไม่ได้ระบุ'}</dd>
                        </div>
                        <div className="pallet-meta-full">
                          <dt>หมายเหตุ</dt>
                          <dd>{registration.notes || 'ไม่มีหมายเหตุ'}</dd>
                        </div>
                      </dl>
                    </div>
                  </article>
                ))}
                {visibleCount < filteredRegistrations.length && (
                  <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
                )}
              </div>
            </>
          )}
        </div>
      </div>
      )}
      {editingRegistration ? (
        <div className="pallet-modal-backdrop" role="presentation" onClick={closeEditModal}>
          <form
            className="pallet-card pallet-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pallet-edit-modal-title"
            onClick={(event) => event.stopPropagation()}
            onSubmit={handleSubmit}
          >
            <div className="pallet-card-header">
              <div>
                <h2 id="pallet-edit-modal-title">แก้ไขพาเลท</h2>
                <p>อัปเดตรายละเอียดพาเลทและตั้งค่าว่าพาเลทใช้งานได้หรือไม่</p>
              </div>
              <button type="button" className="pallet-modal-close" onClick={closeEditModal} aria-label="ปิด">
                x
              </button>
            </div>

            <p className="pallet-edit-code">Pallet code: {editingRegistration.generatedCode}</p>

            <div className="pallet-form-row">
              <label className="pallet-field">
                <span>ประเภทพาเลท</span>
                <select
                  name="palletType"
                  value={form.palletType}
                  onChange={handleFieldChange}
                  disabled
                >
                  {PALLET_TYPE_OPTIONS.map((palletType) => (
                    <option key={palletType} value={palletType}>
                      {palletType}
                    </option>
                  ))}
                </select>
              </label>

              <label className="pallet-field">
                <span>น้ำหนัก</span>
                <input
                  name="weight"
                  type="number"
                  min="0.001"
                  step="0.001"
                  value={form.weight}
                  onChange={handleFieldChange}
                />
              </label>
            </div>

            <label className="pallet-field">
              <span>ลงทะเบียนโดย</span>
              <input
                name="registeredBy"
                value={form.registeredBy}
                onChange={handleFieldChange}
                placeholder="e.g. Warehouse Team"
              />
            </label>

            <label className="pallet-field">
              <span>สถานะ</span>
              <select name="status" value={form.status} onChange={handleFieldChange}>
                {PALLET_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>

            <label className="pallet-field">
              <span>หมายเหตุ</span>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleFieldChange}
                rows={4}
                placeholder="หมายเหตุการจัดการหรือการจัดส่ง (ไม่บังคับ)"
              />
            </label>

            <div className="pallet-actions pallet-modal-actions">
              <button type="submit" className="pallet-primary-btn">
                บันทึกการเปลี่ยนแปลง
              </button>
              <button type="button" className="pallet-secondary-btn" onClick={closeEditModal}>
                ยกเลิก
              </button>
            </div>

            {saveState.message ? (
              <p className={`pallet-feedback pallet-feedback-${saveState.type}`}>{saveState.message}</p>
            ) : null}
          </form>
        </div>
      ) : null}
    </section>
  );
}

